import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { shell } from './shellClasses';

export type WorkspaceHeaderProps = {
  title: string;
  subtitle?: string;
  breadcrumb?: { label: string; path?: string }[];
  primaryActions?: ReactNode;
  secondaryActions?: ReactNode;
  quickFilters?: ReactNode;
};

/**
 * Standardized workspace header chrome.
 * Pages may adopt gradually — do not redesign domain bodies.
 */
export function WorkspaceHeader({
  title,
  subtitle,
  breadcrumb,
  primaryActions,
  secondaryActions,
  quickFilters,
}: WorkspaceHeaderProps) {
  return (
    <header className={`mb-4 space-y-3 border-b ${shell.border} pb-4`}>
      {breadcrumb && breadcrumb.length > 0 && (
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-1 text-sm">
            {breadcrumb.map((c, i) => {
              const last = i === breadcrumb.length - 1;
              return (
                <li key={`${c.label}-${i}`} className="flex items-center gap-1">
                  {i > 0 && (
                    <span aria-hidden className={shell.subtle}>
                      /
                    </span>
                  )}
                  {last || !c.path ? (
                    <span
                      aria-current={last ? 'page' : undefined}
                      className={last ? 'font-medium text-[var(--shell-fg)]' : shell.muted}
                    >
                      {c.label}
                    </span>
                  ) : (
                    <Link to={c.path} className={`${shell.muted} hover:text-[var(--shell-accent)]`}>
                      {c.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-[var(--shell-fg)] sm:text-2xl">
            {title}
          </h1>
          {subtitle && <p className={`mt-1 text-sm ${shell.muted}`}>{subtitle}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {secondaryActions}
          {primaryActions}
        </div>
      </div>
      {quickFilters && <div className="flex flex-wrap items-center gap-2">{quickFilters}</div>}
    </header>
  );
}

/** Optional action bar container under header (slot only). */
export function ActionBar({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <div
      role="toolbar"
      aria-label="Page actions"
      className={`mb-4 flex flex-wrap items-center gap-2 border-b ${shell.border} pb-3`}
    >
      {children}
    </div>
  );
}
